/**
 * VÉRAULT — Proposal Decision section
 *
 * Renders at the bottom of the client-facing Proposal, AFTER the Needed
 * Confirmations summary + Submit. Two paths:
 *
 *   - Approve Proposal — requires all required confirmations complete.
 *     Marks the proposal as client-approved (no booking yet) and notifies
 *     the advisor to move into final pricing + itinerary build.
 *
 *   - Request Changes — available anytime. Opens a free-form note that
 *     gets routed to the advisor (Smart Action Board + Concierge Desk).
 *
 * After approval or a change request, the section locks into a calm
 * "decision recorded" pill so the client can't double-fire.
 */
import React from "react";
import { Loader2, Check, MessageSquare, ShieldCheck, AlertCircle } from "lucide-react";
import { Surface, api } from "../index";
import { toast } from "sonner";

function isAdvisorPreview() {
  if (typeof window === "undefined") return false;
  return (window.location.pathname || "").includes("/portal/advisor/");
}

export default function ProposalDecision({ tripId }) {
  const [stage, setStage] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [changeOpen, setChangeOpen] = React.useState(false);
  const [changeText, setChangeText] = React.useState("");
  const [approveOpen, setApproveOpen] = React.useState(false);
  const [approveAck, setApproveAck] = React.useState(false);

  const isPreview = isAdvisorPreview();

  const fetchStage = React.useCallback(async () => {
    if (!tripId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/client-portal/trips/${tripId}/stage`);
      setStage(data);
    } catch {
      setStage(null);
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  React.useEffect(() => { fetchStage(); }, [fetchStage]);

  React.useEffect(() => {
    const handler = () => fetchStage();
    window.addEventListener("vt:smart-answers:refresh", handler);
    return () => window.removeEventListener("vt:smart-answers:refresh", handler);
  }, [fetchStage]);

  if (loading || !stage) return null;
  // Hide entirely on advisor preview — approval is a real client action.
  if (isPreview) {
    return (
      <Surface variant="paper" padded className="!p-5 my-6" data-testid="proposal-decision-preview">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
          Proposal Decision · advisor preview
        </p>
        <p className="mt-2 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
          The Approve Proposal + Request Changes buttons appear here for the actual client.
          Approval requires all {stage.required_total} required confirmations to be complete.
        </p>
      </Surface>
    );
  }

  const approved = Boolean(stage.proposal_approved_at);
  const changesRequested = Boolean(stage.proposal_changes_requested_at) && !approved;
  const requiredComplete = (stage.required_total || 0) === 0 || stage.required_answered >= stage.required_total;

  // Final recorded state
  if (approved || changesRequested) {
    return (
      <Surface variant="paper" padded className="!p-6 mt-10 space-y-3" data-testid="proposal-decision-recorded">
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--accent-gold))]">
          Proposal Decision
        </p>
        {approved ? (
          <>
            <h2 className="font-serif text-[22px] leading-tight text-[hsl(var(--foreground))]">
              You approved this direction.
            </h2>
            <p className="font-accent italic text-[13.5px] text-[hsl(var(--foreground-muted))] leading-relaxed">
              Antonia received the approval and is moving into final pricing + the confirmed itinerary.
              You&apos;ll see the next step in your Suite Home as soon as it&apos;s ready.
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
              Approved · {new Date(stage.proposal_approved_at).toLocaleDateString("en-US")}
            </p>
          </>
        ) : (
          <>
            <h2 className="font-serif text-[22px] leading-tight text-[hsl(var(--foreground))]">
              You asked for some adjustments.
            </h2>
            <p className="font-accent italic text-[13.5px] text-[hsl(var(--foreground-muted))] leading-relaxed">
              Antonia received your notes and is reviewing. She&apos;ll return with a revised direction shortly.
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
              Requested · {new Date(stage.proposal_changes_requested_at).toLocaleDateString("en-US")}
            </p>
          </>
        )}
      </Surface>
    );
  }

  async function approve() {
    if (!requiredComplete) {
      toast.error(`Please complete the required confirmations first (${stage.required_answered}/${stage.required_total}).`);
      return;
    }
    if (!approveAck) {
      toast.error("Please acknowledge before approving.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/client-portal/trips/${tripId}/proposal/approve`, { acknowledged: true });
      toast.success("Direction approved · advisor notified.");
      setApproveOpen(false);
      await fetchStage();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't approve.");
    } finally {
      setBusy(false);
    }
  }

  async function submitChanges() {
    if (!changeText.trim()) {
      toast.error("Share a quick note so Antonia knows what to adjust.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/client-portal/trips/${tripId}/proposal/request-changes`, { comment: changeText });
      toast.success("Notes sent to your advisor.");
      setChangeOpen(false);
      setChangeText("");
      await fetchStage();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send changes.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Surface variant="paper" padded className="!p-6 mt-10 space-y-5" data-testid="proposal-decision">
      <div className="text-center space-y-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-[hsl(var(--accent-gold))]">
          Proposal Decision
        </p>
        <h2 className="font-serif text-[22px] sm:text-[24px] leading-tight text-[hsl(var(--foreground))]">
          Approve the direction, or ask for changes.
        </h2>
        <p className="font-accent italic text-[13.5px] text-[hsl(var(--foreground-muted))] max-w-xl mx-auto leading-relaxed">
          Once your confirmations are complete, you can approve this direction or request changes
          before anything is held or confirmed.
        </p>
      </div>

      {!requiredComplete && stage.required_total > 0 && (
        <div
          className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200"
          data-testid="proposal-decision-required-incomplete"
        >
          <AlertCircle className="size-4 text-amber-700 mt-0.5 shrink-0" strokeWidth={1.5} />
          <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground))] leading-relaxed">
            Please complete the required confirmations above ({stage.required_answered} of {stage.required_total} done) before approving.
            You can still request changes anytime.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => setApproveOpen(true)}
          disabled={busy || !requiredComplete}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[hsl(var(--accent-gold))]/30 hover:bg-[hsl(var(--accent-gold))]/55 disabled:opacity-40 disabled:cursor-not-allowed font-mono text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--foreground))] border border-[hsl(var(--accent-gold))]/40"
          data-testid="proposal-decision-approve"
        >
          <ShieldCheck className="size-4" strokeWidth={1.5} />
          Approve Proposal
        </button>
        <button
          type="button"
          onClick={() => setChangeOpen((o) => !o)}
          disabled={busy}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[rgba(0,0,0,0.12)] hover:bg-white font-mono text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--foreground))]"
          data-testid="proposal-decision-request-changes"
        >
          <MessageSquare className="size-4" strokeWidth={1.5} />
          Request Changes
        </button>
      </div>

      {approveOpen && (
        <div className="space-y-3 px-4 py-3 rounded-md border border-[hsl(var(--accent-gold))]/40 bg-[hsl(var(--accent-gold))]/[0.05]" data-testid="proposal-decision-approve-dialog">
          <p className="font-accent italic text-[13.5px] text-[hsl(var(--foreground))] leading-relaxed">
            Approving means you accept the direction described above. Nothing will be booked yet —
            Antonia will move into final pricing and the day-by-day itinerary build.
          </p>
          <label className="flex items-start gap-2 font-serif text-[14px] cursor-pointer">
            <input type="checkbox" checked={approveAck} onChange={(e) => setApproveAck(e.target.checked)}
                   className="mt-1" data-testid="proposal-decision-approve-ack" />
            <span>I&apos;m comfortable approving this proposal direction.</span>
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setApproveOpen(false)}
                    className="px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
              Cancel
            </button>
            <button type="button" onClick={approve} disabled={busy || !approveAck}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[hsl(var(--accent-gold))]/35 hover:bg-[hsl(var(--accent-gold))]/55 disabled:opacity-50 font-mono text-[10.5px] uppercase tracking-[0.22em]"
                    data-testid="proposal-decision-approve-confirm">
              {busy ? <Loader2 className="size-3 animate-spin" strokeWidth={1.5} /> : <Check className="size-3.5" strokeWidth={1.5} />}
              Confirm approval
            </button>
          </div>
        </div>
      )}

      {changeOpen && (
        <div className="space-y-3 px-4 py-3 rounded-md border border-[rgba(0,0,0,0.08)] bg-white" data-testid="proposal-decision-change-dialog">
          <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
            Share what you&apos;d like adjusted. Antonia will see your notes in her workspace and respond.
          </p>
          <textarea
            value={changeText}
            onChange={(e) => setChangeText(e.target.value)}
            rows={4}
            placeholder="What should we adjust? Be as specific as you like."
            className="w-full px-3 py-2 rounded-md border border-[rgba(0,0,0,0.08)] bg-white font-serif text-[14px] leading-relaxed"
            data-testid="proposal-decision-change-text"
          />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setChangeOpen(false); setChangeText(""); }}
                    className="px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--foreground-muted))]">
              Cancel
            </button>
            <button type="button" onClick={submitChanges} disabled={busy || !changeText.trim()}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[hsl(var(--foreground))]/90 hover:bg-[hsl(var(--foreground))] text-[hsl(var(--background))] disabled:opacity-50 font-mono text-[10.5px] uppercase tracking-[0.22em]"
                    data-testid="proposal-decision-change-submit">
              {busy ? <Loader2 className="size-3 animate-spin" strokeWidth={1.5} /> : <MessageSquare className="size-3.5" strokeWidth={1.5} />}
              Send notes
            </button>
          </div>
        </div>
      )}
    </Surface>
  );
}
